import { Component, ViewChild, OnInit } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
import { FeedbackType } from 'src/app/general-components/feedback/feedback-type';
import { FeedbackSettings } from 'src/app/general-components/feedback/feedback-settings';
import { Router } from '@angular/router';
import { FeedbackComponent } from 'src/app/general-components/feedback/feedback.component';
import { ErrorResponse } from 'src/app/interfaces/bad-request';
import { HttpErrorResponse } from '@angular/common/http';
import { concat, EMPTY, Observable } from 'rxjs';
import { ReservedSpecialSku } from '../reserved-special-sku';
import { SpecialsService } from '../specials.service';
import { EmptyStats, Stats } from '../suppliers/supplier';
import { NewFestoSpecialComponent } from './new-festo-special/new-festo-special.component';
import { FestoSpecialsTableComponent } from './festo-specials-table/festo-specials-table.component';
import { EmptyFestoSpecial, FestoSpecial } from './festo-special';
import { FestoCombinedData, FestoSpecialsService } from './festo-specials.service';

@Component({
  selector: 'app-festo-specials',
  templateUrl: './festo-specials.component.html',
  styleUrls: ['./festo-specials.component.scss'],
})
export class FestoSpecialsComponent implements OnInit {
  @ViewChild(NewFestoSpecialComponent)
  newFestoSpecialComponent!: NewFestoSpecialComponent;
  @ViewChild(FestoSpecialsTableComponent)
  festoSpecialsTableComponent!: FestoSpecialsTableComponent;
  @ViewChild(FeedbackComponent)
  feedbackComponent!: FeedbackComponent;
  specials: FestoSpecial[] = [];
  stats: Stats = EmptyStats();
  newSpecial: FestoSpecial = EmptyFestoSpecial();
  failedSpecial: FestoSpecial | undefined;
  keepInputs: boolean = false;

  feedbackSettings: FeedbackSettings = {
    identifier: '',
    show: false,
    type: FeedbackType.error,
    message: 'Er is iets misgegaan sorry',
    closeButton: false,
  };
  newSpecialFeedbackIdentifier = 'newSpecialFeedback';
  generalFeedbackIdentifier = 'generalFeedback';

  reservedSpecialSku: ReservedSpecialSku | undefined;

  isLoading: boolean = false;

  constructor(
    private festoSpecialsService: FestoSpecialsService,
    private specialsService: SpecialsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Sequential on purpose: Firefox aborts parallel requests to the VPN-only API hosts,
    // see "Parallel requests to private API hosts" in AGENTS.md.
    concat(this.getReservedSpecialSku(), this.getFestoSpecials()).subscribe();
  }

  getFestoSpecials(): Observable<unknown> {
    this.isLoading = true;
    return this.festoSpecialsService.getCombinedData().pipe(
      tap((combinedData: FestoCombinedData) => {
        this.specials = combinedData.specials;
        this.stats = combinedData.stats;
        this.isLoading = false;
      }),
      catchError((error: Error) => {
        this.updateFeedback(
          this.generalFeedbackIdentifier,
          FeedbackType.error,
          true,
          'Er is iets foutgegaan bij het ophalen van de Festo specials',
          'Terug naar hoofdmenu',
          false
        );
        this.isLoading = false;
        console.error(error);
        return EMPTY;
      })
    );
  }

  getReservedSpecialSku(): Observable<unknown> {
    this.isLoading = true;
    return this.specialsService.getReservedSpecialSku().pipe(
      tap((reservedSpecialSku: ReservedSpecialSku) => {
        this.reservedSpecialSku = reservedSpecialSku;
      }),
      catchError((error: Error) => {
        this.updateFeedback(
          this.generalFeedbackIdentifier,
          FeedbackType.error,
          true,
          'Er kon geen special gereserveerd worden, probeer het later nog een keer',
          'Terug naar hoofdmenu',
          false
        );
        this.isLoading = false;
        console.error(error);
        return EMPTY;
      })
    );
  }

  setKeepInput(keepInputs: boolean) {
    this.keepInputs = keepInputs;
  }

  createSpecial(special: FestoSpecial) {
    this.isLoading = true;
    this.festoSpecialsService.createFestoSpecial(special).subscribe({
      next: (newSpecial: FestoSpecial) => {
        this.specials.push(newSpecial);

        this.festoSpecialsTableComponent.sortByField('creationDate', false);
        this.updateFeedback(
          this.newSpecialFeedbackIdentifier,
          FeedbackType.success,
          true,
          `De special ${newSpecial.sku} is aangemaakt!`,
          undefined,
          false
        );
        this.feedbackComponent.fadeOut(2000);
        this.getReservedSpecialSku().subscribe();
        if (!this.keepInputs) {
          this.newFestoSpecialComponent.clearSpecial();
        } else {
          this.newFestoSpecialComponent.clearUniqueFields();
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.failedSpecial = special;
        console.error(error.error);
        if (error.status == 400) {
          const badRequestError = error.error as ErrorResponse;
          const errorException = badRequestError.exception;
          if (errorException?.includes('DoubleDescriptionException')) {
            const SKUs = this.getSKUsFromError(errorException);
            this.updateFeedback(
              this.newSpecialFeedbackIdentifier,
              FeedbackType.error,
              true,
              `Er bestaat al een special met de omschrijving "${special.description}": ${SKUs}`,
              'Aanpassen en opnieuw proberen',
              true
            );
          } else {
            this.updateFeedback(
              this.newSpecialFeedbackIdentifier,
              FeedbackType.error,
              true,
              'Er klopte *iets* niet aan de special',
              'Aanpassen en opnieuw proberen',
              true
            );
          }
        } else {
          this.updateFeedback(
            this.newSpecialFeedbackIdentifier,
            FeedbackType.error,
            true,
            'Er is iets fout gegaan bij aanmaken van de special',
            undefined,
            true
          );
        }
        this.isLoading = false;
      },
    });
  }

  getSKUsFromError(errorException: string) {
    const parts = errorException.match(/\[(.*?)\]/);
    return parts ? parts[1] : 'Niet beschikbaar';
  }

  retryFailedSpecial() {
    if (this.failedSpecial) {
      this.feedbackSettings.show = false;
      this.failedSpecial.creationDate = '';

      this.newFestoSpecialComponent.retryFailedSpecial(this.failedSpecial);
    }
  }

  updateFeedback(
    identifier: string,
    type: FeedbackType,
    show: boolean,
    message: string,
    buttonText: string | undefined,
    closeButton: boolean
  ) {
    this.feedbackSettings.identifier = identifier;
    this.feedbackSettings.type = type;
    this.feedbackSettings.show = show;
    this.feedbackSettings.message = message;
    this.feedbackSettings.buttonText = buttonText;
    this.feedbackSettings.closeButton = closeButton;
  }

  handleFeedbackOutput(identifier: string) {
    if (identifier == this.newSpecialFeedbackIdentifier) {
      this.retryFailedSpecial();
    } else {
      this.returnHome();
    }
  }

  returnHome() {
    void this.router.navigate(['home']);
  }

  feedbackType() {
    return FeedbackType;
  }
}
